import * as path from 'path';
import * as stream from 'stream';
import ActiveError from './error';
import getMime from './util.mime';
import { ErrorLog, BufferType } from './index';
/**
 * 处理相应类
 */
export type Param<Value extends object | string | BufferType | string = object> = {
	callback?: string, cb?: string,
	error?: any, statusCode?: number, value?: Value, stream?: stream.Readable, mime?: string,
	download?: string | boolean, transfer?: string, appPath?: string, file?: string, appId?: string,
	errId?: string
}
export default class Response<Value extends object | string | BufferType | string = object> {
	/**
	 * 创建错误对象
	 * @param id 错误Id
	 * @param msg 错误信息
	 */
	static Error(id: string, msg?: string): ActiveError { return new ActiveError(id, msg); }
	/**
	 * 创建并抛出错误对象
	 * @param id 错误Id
	 * @param msg 错误信息
	 */
	static throw(id: string, msg?: string): never { throw this.Error(id, msg); }
	/**
	 * 创建错误对象
	 * @param id 错误Id
	 * @param msg 错误信息
	 */
	Error(id: string, msg?: string): ActiveError {
		const error = new ActiveError(id, msg);
		let errId = this.errId;
		if (errId) { error.setAppId(errId); }
		return error;
	}
	/**
	 * 创建并抛出错误对象
	 * @param id 错误Id
	 * @param msg 错误信息
	 */
	throw(id: string, msg?: string): never { throw this.Error(id, msg); }
	_errId: string = '';
	_appId: string = '';
	_appPath: string = '';
	_error: any;
	_transfer: string = '';
	_download: string | boolean = '';
	_file: string = '';
	_value?: Value | object | stream.Readable;
	_statusCode: number = 0;
	_callback: string = '';
	_mime: string = '';
	_render: string = '';
	constructor(arg: string);
	constructor(arg: Param<Value>);
	constructor(arg: Param<Value> | string) {
		if (typeof arg === 'string') {
			arg = {callback: arg};
		} else if (!arg) {
			arg = {};
		}
		const {
			callback, cb,
			error, statusCode, value, stream, mime,
			download, transfer, appPath, file, appId,
			errId
		} = arg;
		this._callback = typeof callback === 'string' && callback || typeof cb === 'string' && cb || '';
		this.error = error;
		this.statusCode = statusCode || 0;
		this.mime = mime || '';
		this.download = download || '';
		this.transfer = transfer || '';
		this.appPath = appPath || '';
		this.file = file || '';
		this.errId = errId || appId || '';
		this.appId = appId || '';
		this.value = value || stream as any;
	}
	/** 错误用应用Id */
	get errId(): string { return this._errId; }
	/** 应用Id */
	get appId(): string { return this._appId; }
	/** 应用路径 */
	get appPath(): string { return this._appPath; }
	/** 错误信息 */
	get error(): any { return this._error; }
	/** 文件下载名 */
	get download(): string | boolean { return this._error || this._transfer ? '' : this._download; }
	/** 302 跳转 目标地址 */
	get transfer(): string { return this._error ? '' : this._transfer; }
	/** 要下载的文件的服务器地址 */
	get file(): string { return this._error ? '' : this._file; }
	/** 相应体(普通对象或文本) */
	get value(): Value | undefined { return this._error || this._value instanceof stream.Readable ? undefined : this._value as Value; }
	/** 相应体(流) */
	get stream(): stream.Readable | undefined { return !this._error && this._value instanceof stream.Readable ? this._value : undefined;}
	/** 状态码 */
	get statusCode(): number {
		let error = this._error;
		if (error) {
			return !error.error ? 500 : error.statusCode || this._statusCode || 200;
		}
		return this._transfer ? 302 : this._statusCode || 200;
	}
	/** 相应 MIME 类型 */
	get mime(): string {
		if (this._error) { return this._callback ? 'application/javascript' : 'application/json'; }
		if (this._transfer) { return ''; }
		if (this._mime && this._mime[0] !== '.') { return this._mime; }
		if (this._mime || this._file) {
			return  getMime(this._mime) || getMime(path.extname(this._file)) || 'application/octet-stream';
		}
		if (this._value instanceof stream.Readable) { return ''; }
		if (this._value instanceof Buffer) { return ''; }
		return this._render ? 'text/html' : this._callback ? 'application/javascript' : 'application/json';
	}
	/**
	 * 通过输出函数输出错误信息
	 * @param echo 输出函数
	 */
	echoError(echo: ErrorLog) {
		let error = this._error;
		if (error && !error.error) {
			echo(error);
		}
	}
	get body() {
		let error = this._error, value = this._value, cb = this._callback;
		if (error) {
			value = error.error ? error : new ActiveError(':system:');
		}
		if (!value) { return; }
		if (value instanceof ArrayBuffer) { return value; }
		if (value instanceof SharedArrayBuffer) { return value; }
		if (value && (value as {buffer?: ArrayBuffer}).buffer instanceof ArrayBuffer) { return (value as {buffer?: ArrayBuffer}).buffer; }
		if (value && (value as {buffer?: SharedArrayBuffer}).buffer instanceof SharedArrayBuffer) { return (value as {buffer?: SharedArrayBuffer}).buffer; }
		if (value instanceof stream.Readable) { return value; }
		if (value instanceof Buffer) { return value; }
		let body: string = '';
		try {
			body = JSON.stringify(value);
		} catch(e) {
			body = `{error:":system:", msg:"系统错误!"}`;
		}
		return cb && (!this._mime || this._mime === 'application/javascript') ? `${cb}(${body})` : body;
	}
	get filePath(): string {
		if (this._error) {
			return '';
		}
		let file = this._file;
		if (!file) {
			return '';
		}
		let appPath = this._appPath;
		if (appPath) {
			if (file[0] === '/' || appPath[appPath.length - 1] === '/') {
				file = appPath + file;
			} else {
				file = appPath + '/' + file;
			}
		}
		return file;
	}
	get renderFile() {
		return this._render;
	}

	set errId(v: string) { this._errId = v ? String(v) : ''; }
	set appId(v: string) { this._appId = v ? String(v) : ''; }
	set appPath(v: string) { this._appPath = v ? String(v) : ''; }
	set error(v: any) {
		if (typeof v === 'string') {
			v = new ActiveError(v);
		} else if (v && Array.isArray(v) && typeof v[0] === 'string') {
			v = new ActiveError(...v as [string, ...any[]]);
		}
		if (this.errId && v && v instanceof ActiveError) { v.setAppId(this.errId); }
		this._error = v;
	}
	set statusCode(v: number) {
		if (typeof v === 'number' && v >= 200 && v < 1000) {
			this._statusCode = Math.floor(v);
		}
	}
	set mime(v: string) {
		if (v && typeof v === 'string') {
			this._mime = v;
		}
	}
	set download(v: string | boolean) {
		if (['string', 'boolean'].includes(typeof v)) {
			this._download = v;
		}
	}
	set transfer(v: string) {
		if (v && typeof v === 'string') {
			this._render = '';
			this._file = '';
			this._value = undefined;
			this._transfer = v;
		}
	}
	set file(v: string) {
		if (v && typeof v === 'string') {
			this._render = '';
			this._transfer = '';
			this._value = undefined;
			this._file = v;
		}
	}
	set value(v: Value | undefined) {
		if (v) {
			this._render = '';
			this._file = '';
			this._transfer = '';
			this._value = v;
		}
	}
	set stream(v: stream.Readable | undefined) {
		if (v) {
			this._render = '';
			this._file = '';
			this._transfer = '';
			this._value = v;
		}
	}
	render(path: string, data?: object, mime?: string): this;
	render(path: string, mime?: string): this;
	render(path: string, data?: object | string, mime?: string): this {
		if (typeof data === 'string') {
			mime = data || mime;
		}
		if (path) {
			this._render = path;
			this._file = '';
			this._transfer = '';
			if (data && typeof data === 'object') {
				this._value = data;
			}
			if (mime && typeof mime === 'string') {
				this._mime = mime;
			}
		}
		return this;
	}
}
